import { Component, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { ColumnFilterInfo, ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { ProductSiteDTO } from 'src/app/models/Products/productSiteDTO';
import { TranslateService } from '@ngx-translate/core';
import { TableConfigService } from 'src/app/shared/services/table-config/table-config.service';
import { ProductService } from 'src/app/shared/services/products/products/product.service';
import { tap, catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState, sitesStateSitesList } from 'src/app/store/reducers';
import { setSnackbar } from 'src/app/store/actions/messages/message.actions';
import { SnackBarUtils } from 'src/app/shared/utils/snackbarUtils';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SiteDTO } from 'src/app/models/Sites/siteDTO';
import { LinkResult, ProductSitesDialogComponent } from '../dialogs/product-sites-dialog/product-sites-dialog.component';
import { LinkSiteToProductsDTO, ProductDTO, ProductSiteEditDTO } from 'src/app/models/Products/productDTO';
import { EditRowDataDialogComponent } from 'src/app/shared/modules/dialogs/edit-row-data-dialog/edit-row-data-dialog.component';
import { SiteProductDTO } from '../../../models/Products/productDTO';
import { FailedLinkProductSitesDialogComponent } from '../dialogs/failed-link-product-sites-dialog/failed-link-product-sites-dialog.component';

const USAGE_CODE = "PRODUCT.SITES";

interface ProductSiteView extends SiteProductDTO {
  sitePrice: string;
}

@Component({
  selector: 'product-sites-list',
  templateUrl: './product-sites-list.component.html',
  styleUrls: ['./product-sites-list.component.scss']
})
export class ProductSitesListComponent implements OnDestroy{
  @Input() set product(product: ProductDTO) {
    if(!product) {
      return;
    }
    this._product = product;
    this.setTableTitle();
    this.setDialogData();
    this.getProductSitesList(product.code);
  }
  @Input() permissionResource: string;

  _product: ProductDTO;

  isLoading$ = new BehaviorSubject<boolean>(false);
  totalProductSites$ = new BehaviorSubject<number>(0);
  columnInfo$: Observable<ColumnFilterInfo[]>;

  productSitesSub: Subscription;
  productSitesList: ProductSiteView[];
  excelProductSitesList$: Observable<ProductSiteDTO[]>;

  loadedSites$: Observable<SiteDTO[]>;
  sitesSub: Subscription;

  tableTitle: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Products.siteCode",
      field: "siteCode",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Products.siteIdentifier",
      field: "siteIdentifier",
      fieldType: FieldType.STRING,
      searchable: false,
    },
    {
      header: "Products.siteDescription",
      field: "siteDescription",
      fieldType: FieldType.STRING,
      searchable: false,
    },
    {
      header: "Products.sitePrice",
      field: "sitePrice",
      fieldType: FieldType.STRING,
      searchable: false,
    },
    {
      header: "Products.favorite",
      field: "favorite",
      fieldType: FieldType.BOOLEAN,
      searchable: false,
    },
    {
      header: "Products.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
      searchable: false,
    },
    {
      header: "Products.createdBy",
      field: "createdBy",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Products.updatedBy",
      field: "updatedBy",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },

  ];

  productSiteDialogComponent = ProductSitesDialogComponent;
  productSiteDialogData: object;

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private productService: ProductService,
    private dialog: MatDialog,
  ) {
    this.loadedSites$ = store.pipe(select(sitesStateSitesList));
  }
  
  setTableTitle() {
    this.tableTitle = this.translate.instant("Products.productSites");
  }

  setDialogData() {
    this.productSiteDialogData = {
      productCode: this._product.code,
      selectedSites: null,
    };
  }

  getProductSitesList(
    code: string,
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent, 
    filterEvent?: FilterEvent
  ) {
    this.isLoading$.next(true);

    const { pageIndex, pageSize } = pageEvent || {};

    this.productSitesSub = this.productService
      .getProductSiteList(
        code,
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent,
      )
      .pipe(
        mergeMap(({result}) => 
          this.loadedSites$.pipe(
            take(1),
            map((sites) => {
              const productSites = result.map((productSite: ProductSiteDTO) => {
                const site: SiteDTO = sites.find((site) => site.code === productSite.siteCode);
                return {
                  ...productSite,
                  siteDescription: site.description,
                  siteIdentifier: site.siteNumber,
                }
              })
              return productSites;
            })
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Products.productSitesLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe((productSites) => {
        const treatedProductSites = this.treatProductSitesList(productSites);
        this.productSitesList = treatedProductSites;
        this.productSiteDialogData = {
          ...this.productSiteDialogData,
          selectedSites: new Set<string>(treatedProductSites.map(productSite => productSite.siteCode)),
        };
      })
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getProductSitesList(
      this._product.code,
      pageEvent,
      sortEvent,
      searchEvent,
      filterEvent,
    );
  }

  onGetExportData(tableEvent: TableEvent) {
    this.getProductSitesList(this._product.code);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  onEditRow(row: ProductSiteDTO) {
    const dialogRef = this.dialog.open(EditRowDataDialogComponent, {
      width: '650px',
      data: {
        productCode: this._product.code,
        productDescription: this._product.description,
        unitPriceGross: row.unitPriceGross,
        currentPrice: row.unitPriceGross,
        token: row.token,
        isFuel: this._product.isFuel,
        unitPriceGrossReference: this._product.unitPriceGross,
        fccProductCode: row.fccProductCode,
        enabled: row.enabled,
        favorite: row.favorite,
      },
      autoFocus: true,
    });

    dialogRef.afterClosed().subscribe((updated: any) => {
      if (!updated) {
        return;
      }
      const updatedProduct = {
        siteCode: row.siteCode,
        productCode: this._product.code,
        // promptPrice: false,
        enabled: updated.enabled,
        isFuel: this._product.isFuel,
        unitPriceGross: updated.unitPriceGross,
        favorite: updated.favorite
      }
      this.updateProduct(updatedProduct);
    });
  }

  onDeleteRow(siteCode: string) {
    const unlinkData: LinkSiteToProductsDTO = {
      siteCode: siteCode,
      productsToLinkToSite: {
        productCode: this._product.code,
        shouldBeLinked: false,
      },
    };

    this.linkOrUnlinkProductSites([unlinkData]);
  }

  treatProductSitesList(products: SiteProductDTO[]): ProductSiteView[] {
    const treatedProducts = products.map(product => {
      return {
        ...product,
        sitePrice: 
          (product.unitPriceGross && product.unitPriceGross > 0)
            ? product.unitPriceGross.toString()
            : `${product.unitPriceGrossReference} (Preço Ref.)`,
      }
    });

    return treatedProducts;
  }
  
  private updateProduct(product: ProductSiteEditDTO): void {
    this.isLoading$.next(true);
    this.productService.updateSiteProducts([product])
    .pipe(
      catchError((error) => {
        this.store.dispatch(
          setSnackbar(
            SnackBarUtils.setSnackBar(
              this.translate.instant(error ?? "Products.productSitesUpdateError"),
              SnackbarType.ERROR
            )
          )
        );
        return EMPTY;
      }),
      finalize(() => {this.isLoading$.next(false);})
    )
    .subscribe((result) => {
      this.store.dispatch(
        setSnackbar(
          SnackBarUtils.setSnackBar(
            this.translate.instant("Products.productSitesUpdateSuccess"),
            SnackbarType.SUCCESS
          )
        )
      );
      this.getProductSitesList(this._product.code);
    });
  }

  private linkOrUnlinkProductSites(linkData: LinkSiteToProductsDTO[]): void {
    this.isLoading$.next(true);
    this.productService.linkOrUnlinkSiteProducts(linkData)
      .pipe(
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant(error ?? "Products.productSitesLinkError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => {this.isLoading$.next(false);}),
      )
      .subscribe(({result}) => {
        if (result.errors && Object.keys(result.errors).length) {
          this.openFailedToLinkProductsDialog(result);
        } else {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Products.productSitesLinkSuccess"),
                SnackbarType.SUCCESS
              )
            )
          );
        };
        this.getProductSitesList(this._product.code);
      });
  }

  openFailedToLinkProductsDialog(result: LinkResult) {
    const dialogData: MatDialogConfig = {
      width: '900px',
      panelClass: 'failed-link-sites-dialog',
      autoFocus: false,
      data: {
        result: result,
      },
    };

    this.dialog.open(FailedLinkProductSitesDialogComponent, dialogData);
  }

  ngOnDestroy(): void {
    if (this.productSitesSub) {
      this.productSitesSub.unsubscribe();
    }
  }
}
